import React, { useState } from 'react';
import { 
  ArrowLeft, LayoutTemplate, Search, X, Boxes, Database, Terminal, 
  ChevronRight, CheckCircle2, Copy, Code, Wand2, LoaderCircle, Rocket,
  Eye, EyeOff, Key, User, Server, Plus
} from 'lucide-react';
import { generateRandomSuffix, validateSubdomainPrefix } from '../../utils/subdomainHelper';

export default function TemplateDeploy({ 
  user, onStatusMessage, onBack, api, 
  beginDeploymentTracking, refreshDeploymentSession, setCurrentSessionProgress,
  onTabChange
}) {
  const [templateStep, setTemplateStep] = useState(1);
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [templateAppName, setTemplateAppName] = useState('');
  const [templateAppNameError, setTemplateAppNameError] = useState('');
  const [platformSuffix] = useState(() => generateRandomSuffix());
  const [subdomainPrefix, setSubdomainPrefix] = useState('');
  const [subdomainError, setSubdomainError] = useState('');
  const [templateSelected, setTemplateSelected] = useState('node-mongo');
  const [templateDomain, setTemplateDomain] = useState('');
  const [templateDomainError, setTemplateDomainError] = useState('');

  const handleSubdomainChange = (e) => {
    const val = e.target.value;
    setSubdomainPrefix(val);
    const res = validateSubdomainPrefix(val);
    setSubdomainError(res.valid ? '' : res.error);
  };

  const validateTemplateDomain = (val) => {
    if (!val || !val.trim()) {
      setTemplateDomainError('');
      return true;
    }
    if (/devorants\.com$/i.test(val.trim())) {
      setTemplateDomainError('devorants.com subdomains cannot be used as custom domains.');
      return false;
    }
    setTemplateDomainError('');
    return true;
  };

  const handleTemplateDomainChange = (e) => {
    const val = e.target.value;
    setTemplateDomain(val);
    validateTemplateDomain(val);
  };
  const [selectedTemplatePort, setSelectedTemplatePort] = useState('');
  const [templateInternalPort, setTemplateInternalPort] = useState('');
  const [templateUserPorts, setTemplateUserPorts] = useState('');
  const [templateConnectionString, setTemplateConnectionString] = useState('');
  const [templateDbPassword, setTemplateDbPassword] = useState('');
  const [templateCpu, setTemplateCpu] = useState('1.0');
  const [templateMemory, setTemplateMemory] = useState('1024m');
  const [showDbPassword, setShowDbPassword] = useState(false);
  const [enableTemplateVolume, setEnableTemplateVolume] = useState(false);
  const [templateVolumeName, setTemplateVolumeName] = useState('');
  const [templateExtraPorts, setTemplateExtraPorts] = useState(null);
  const [templateResolvedDomain, setTemplateResolvedDomain] = useState(null);
  const [templateVscodeLink, setTemplateVscodeLink] = useState('');
  const [templateAntigravityLink, setTemplateAntigravityLink] = useState('');
  const [templateCursorLink, setTemplateCursorLink] = useState('');
  const [activeIdeDropdown, setActiveIdeDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [startingIde, setStartingIde] = useState(false);

  const validateTemplateAppName = (val) => {
    if (!val) {
      setTemplateAppNameError('');
      return false;
    }
    if (/\s/.test(val)) {
      setTemplateAppNameError('Spaces are not allowed. Use hyphens (-) instead.');
      return false;
    }
    if (/[A-Z]/.test(val)) {
      setTemplateAppNameError('Uppercase letters are not allowed. Use lowercase only.');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(val)) {
      setTemplateAppNameError('Only lowercase letters, numbers, and hyphens (-) are allowed.');
      return false;
    }
    setTemplateAppNameError('');
    return true;
  };

  const handleTemplateAppNameChange = (e) => {
    const val = e.target.value;
    setTemplateAppName(val);
    validateTemplateAppName(val);
  };

  const handlePortChange = (value) => {
    setSelectedTemplatePort(value);
  };

  // Multi-entry point management
  const [entryPoints, setEntryPoints] = useState([]);

  const handleAddEntryPoint = () => {
    setEntryPoints([...entryPoints, { port: '', role: 'service', domain: '' }]);
  };

  const handleUpdateEntryPoint = (idx, field, value) => {
    const updated = [...entryPoints];
    updated[idx] = { ...updated[idx], [field]: field === 'port' ? (value === '' ? '' : Number(value)) : value };
    setEntryPoints(updated);
  };

  const handleRemoveEntryPoint = (idx) => {
    setEntryPoints(entryPoints.filter((_, i) => i !== idx));
  };

  const handleTemplateDeploy = async (e) => {
    e.preventDefault();
    if (!templateAppName || !validateTemplateAppName(templateAppName)) {
      onStatusMessage(templateAppNameError || 'Please provide a valid Application Name', 'error');
      return;
    }

    if (subdomainPrefix && !validateSubdomainPrefix(subdomainPrefix).valid) {
      onStatusMessage(subdomainError || 'Invalid subdomain format.', 'error');
      return;
    }

    if (templateDomain && !validateTemplateDomain(templateDomain)) {
      onStatusMessage(templateDomainError || 'devorants.com subdomains cannot be used as custom domains.', 'error');
      return;
    }

    const resolvedPrefix = (subdomainPrefix && subdomainPrefix.trim())
      ? subdomainPrefix.trim().toLowerCase()
      : (templateAppName || 'app').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    const autoPlatformDomain = `${resolvedPrefix}-${platformSuffix}.devorants.com`;
    const finalDomain = (templateDomain && templateDomain.trim()) ? templateDomain.trim() : autoPlatformDomain;

    setLoading(true);
    const deploymentId = beginDeploymentTracking('docker', templateAppName);

    try {
      const volumeData = enableTemplateVolume && templateVolumeName
        ? [`${templateVolumeName}:/var/lib/mysql`]
        : undefined;

      const parsedUserPorts = templateUserPorts ? templateUserPorts.split(',').map(p => p.trim()).filter(p => p) : undefined;
      const validEntryPoints = entryPoints.filter(ep => ep.port && Number(ep.port) > 0);

      api.deployTemplate(templateAppName, templateSelected, {
        deploymentId,
        domain: finalDomain,
        publicPort: selectedTemplatePort ? parseInt(selectedTemplatePort) : undefined,
        internalPort: templateInternalPort ? parseInt(templateInternalPort) : undefined,
        userPorts: parsedUserPorts,
        entryPoints: validEntryPoints.length > 0 ? validEntryPoints : undefined,
        volumes: volumeData,
        dbPassword: templateDbPassword,
        dbName: templateAppName,
        cpus: templateCpu,
        memory: templateMemory
      }).then(
        (response) => {
          if (response && response.cancelled) return;
          setTemplateAppName('');
          setSubdomainPrefix('');
          setSubdomainError('');
          setTemplateDomain('');
          setTemplateDomainError('');
          setSelectedTemplatePort('');
          setTemplateDbPassword('');
          setEnableTemplateVolume(false);
          setTemplateVolumeName('');
          setTemplateVscodeLink(response.vscodeLink || '');
          setTemplateAntigravityLink(response.antigravityLink || '');
          setTemplateCursorLink((response.vscodeLink || '').replace('vscode://', 'cursor://'));
          setTemplateExtraPorts(response.extraPorts || null);
          setTemplateResolvedDomain(response.domain || null);

          if (response.connectionInfo) {
              const note = response.connectionInfo.note || '';
              const displayHost = response.domain || window.location.hostname;
              const connStr = response.connectionInfo.type === 'mysql'
                ? `mysql://${response.connectionInfo.user}:${response.connectionInfo.password}@${displayHost}:${response.connectionInfo.port}/${response.connectionInfo.database}`
                : response.connectionInfo.uri || '';
              setTemplateConnectionString(`${connStr}\n${note}`);
          } else if (response.connectionString) {
              const displayHost = response.domain || window.location.hostname;
              const finalString = response.connectionString.replace('YOUR_SERVER_IP', displayHost);
              setTemplateConnectionString(finalString);
          } else {
              onStatusMessage(response.message || 'Template deployment completed successfully!', 'success');
          }

          // Navigate to dashboard after a short delay so progress tracking can finish
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('switch-to-dashboard'));
            if (onTabChange) onTabChange('deployments');
          }, 1500);
        },
        (error) => {
          if (error && error.message === 'cancelled') return;
          onStatusMessage(error.message || 'Template deployment failed', 'error');
        }
      ).finally(() => {
        setLoading(false);
        setCurrentSessionProgress(100);
        refreshDeploymentSession(deploymentId);
      });
    } catch (err) {
      onStatusMessage(`Error: ${err.message}`, 'error');
      setLoading(false);
    }
  };

  return (
    <div className="vercel-empty-state-container animate-fade-in" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', textAlign: 'center' }}>
      {templateStep === 1 ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1rem' }}>
            <button className="vercel-back-btn-new" onClick={() => { onBack(); setTemplateStep(1); }} style={{ margin: 0 }}>
              <ArrowLeft size={16} />
              <span>Back to selection</span>
            </button>
          </div>

          <div className="vercel-empty-icon-wrapper">
            <LayoutTemplate size={24} />
          </div>

          <h2 className="vercel-empty-title">Select Stack Template</h2>
          <p className="vercel-empty-subtitle">
            Choose a pre-configured stack template to get started with your environment.
          </p>

          <div style={{ textAlign: 'left', marginTop: '1.5rem' }}>
            {/* Search Bar Input */}
            <div style={{
              position: 'relative',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Search size={16} style={{ position: 'absolute', left: '1rem', color: 'var(--text-muted)' }} />
              <input 
                type="text" 
                placeholder="Search stack templates..." 
                value={templateSearchQuery}
                onChange={(e) => setTemplateSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.65rem 1rem 0.65rem 2.6rem',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  background: 'var(--surface-2)',
                  color: 'var(--text-main)',
                  fontSize: '0.88rem',
                  outline: 'none'
                }}
              />
              {templateSearchQuery && (
                <button 
                  type="button"
                  onClick={() => setTemplateSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '0.75rem',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.25rem'
                  }}
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Scrollable Template List Container */}
            <div 
              className="vercel-empty-list-card"
              style={{
                maxHeight: '420px',
                overflowY: 'auto',
                scrollbarWidth: 'thin'
              }}
            >
              {[
                {
                  id: 'node-mongo',
                  name: 'Node.js + MongoDB',
                  description: 'All-in-one container with Node.js and MongoDB (localhost:27017)',
                  icon: <Boxes size={18} style={{ color: '#0070f3' }} />,
                  badge: 'Popular'
                },
                {
                  id: 'node-mysql',
                  name: 'Node.js + MySQL',
                  description: 'All-in-one container with Node.js and MySQL (localhost:3306)',
                  icon: <Database size={18} style={{ color: '#f5a623' }} />,
                  badge: 'SQL'
                },
                {
                  id: 'custom-mysql',
                  name: 'Standalone MySQL',
                  description: 'Lightweight MySQL database container for external connections',
                  icon: <Database size={18} style={{ color: '#10b981' }} />,
                  badge: 'DB Only'
                },
                {
                  id: 'alma-linux',
                  name: 'Alma Linux 9',
                  description: 'Clean Alma Linux 9 environment acting as a lightweight VPS',
                  icon: <Terminal size={18} style={{ color: '#ef4444' }} />,
                  badge: 'OS'
                }
              ].filter(tpl => 
                tpl.name.toLowerCase().includes(templateSearchQuery.toLowerCase()) || 
                tpl.description.toLowerCase().includes(templateSearchQuery.toLowerCase()) ||
                (tpl.badge && tpl.badge.toLowerCase().includes(templateSearchQuery.toLowerCase()))
              ).map((tpl) => {
                const isSelected = templateSelected === tpl.id;
                return (
                  <div 
                    key={tpl.id}
                    onClick={() => setTemplateSelected(tpl.id)}
                    className="vercel-empty-list-item"
                    style={{
                      background: isSelected ? 'rgba(var(--primary-rgb), 0.04)' : 'transparent',
                      borderLeft: isSelected ? '3px solid var(--primary)' : '3px solid transparent',
                      paddingLeft: isSelected ? 'calc(1.15rem - 3px)' : '1.15rem',
                      cursor: 'pointer'
                    }}
                  >
                    <div className="vercel-empty-list-icon">{tpl.icon}</div>
                    <div className="vercel-empty-list-info">
                      <h3 style={{ color: isSelected ? 'var(--primary)' : 'var(--text-main)', fontWeight: isSelected ? 700 : 600 }}>{tpl.name}</h3>
                      <p>{tpl.description}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {tpl.badge && (
                        <span style={{
                          background: isSelected ? 'rgba(var(--primary-rgb), 0.12)' : 'rgba(0, 0, 0, 0.05)',
                          color: isSelected ? 'var(--primary)' : 'var(--text-muted)',
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          padding: '0.15rem 0.5rem',
                          borderRadius: '4px',
                          textTransform: 'uppercase'
                        }}>{tpl.badge}</span>
                      )}
                      <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: isSelected ? '5px solid var(--primary)' : '2px solid var(--border)',
                        background: 'var(--surface)',
                        transition: 'all 0.2s ease'
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <button 
              type="button"
              onClick={() => setTemplateStep(2)}
              className="vercel-empty-btn"
              style={{
                width: '100%',
                padding: '0.75rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                background: 'var(--text-main)',
                color: 'var(--bg)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                marginTop: '1.25rem',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
              }}
            >
              <span>Next: Configure Environment</span>
              <ChevronRight size={18} />
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '1rem' }}>
            <button className="vercel-back-btn-new" onClick={() => setTemplateStep(1)} style={{ margin: 0 }}>
              <ArrowLeft size={16} />
              <span>Back to template selection</span>
            </button>
          </div>

          <div className="vercel-empty-icon-wrapper">
            <Boxes size={24} />
          </div>

          <h2 className="vercel-empty-title">General Configuration</h2>
          <p className="vercel-empty-subtitle">
            Configure project parameters for <strong>
              {templateSelected === 'node-mongo' && 'Node.js + MongoDB'}
              {templateSelected === 'node-mysql' && 'Node.js + MySQL'}
              {templateSelected === 'custom-mysql' && 'Standalone MySQL'}
              {templateSelected === 'alma-linux' && 'Alma Linux 9'}
            </strong>.
          </p>

          <form onSubmit={handleTemplateDeploy} style={{ textAlign: 'left', marginTop: '1.5rem' }}>
            <div className="vercel-empty-list-card" style={{ padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.35rem', display: 'block' }}>
                    Application Name <span className="required-star">*</span>
                  </label>
                  <input 
                    type="text" 
                    placeholder="e.g. my-template-app" 
                    required 
                    value={templateAppName} 
                    onChange={handleTemplateAppNameChange}
                    style={{ 
                      width: '100%', 
                      padding: '0.65rem 0.85rem', 
                      borderRadius: '8px', 
                      border: templateAppNameError ? '1px solid var(--danger, #ef4444)' : '1px solid var(--border)', 
                      background: 'var(--surface-2)', 
                      color: 'var(--text-main)', 
                      fontSize: '0.9rem' 
                    }}
                  />
                  {templateAppNameError ? (
                    <p className="form-feedback" style={{ fontSize: '0.75rem', color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                      {templateAppNameError}
                    </p>
                  ) : (
                    <p className="form-feedback" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Use lowercase, numbers, and hyphens only.</p>
                  )}
                </div>

                {templateSelected !== 'custom-mysql' && (
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.35rem', display: 'block' }}>
                      Platform Subdomain
                    </label>
                    <div className={`platform-subdomain-group ${subdomainError ? 'has-error' : ''}`}>
                      <input 
                        type="text" 
                        placeholder={templateAppName ? templateAppName.toLowerCase() : "e.g. my-app"} 
                        value={subdomainPrefix} 
                        onChange={handleSubdomainChange}
                      />
                      <span className="platform-subdomain-suffix">
                        {`-${platformSuffix}.devorants.com`}
                      </span>
                    </div>
                    {subdomainError ? (
                      <p className="form-feedback" style={{ fontSize: '0.75rem', color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                        {subdomainError}
                      </p>
                    ) : (
                      <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                        Preview: <strong style={{ color: 'var(--primary, #6366f1)' }}>{`${(subdomainPrefix || templateAppName || 'app').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-')}-${platformSuffix}.devorants.com`}</strong>
                      </p>
                    )}
                  </div>
                )}

                {templateSelected !== 'custom-mysql' ? (
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.35rem', display: 'block' }}>
                      Custom Domain (optional)
                    </label>
                    <input 
                      type="text" 
                      placeholder="e.g. app.example.com" 
                      value={templateDomain} 
                      onChange={handleTemplateDomainChange}
                      style={{ 
                        width: '100%', 
                        padding: '0.65rem 0.85rem', 
                        borderRadius: '8px', 
                        border: templateDomainError ? '1px solid var(--danger, #ef4444)' : '1px solid var(--border)', 
                        background: 'var(--surface-2)', 
                        color: 'var(--text-main)', 
                        fontSize: '0.9rem' 
                      }}
                    />
                    {templateDomainError ? (
                      <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                        {templateDomainError}
                      </p>
                    ) : (
                      <p className="form-feedback" style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                        Your own registered domain. Leave blank to use devorants.com subdomain.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.35rem', display: 'block' }}>
                      Database Password <span className="required-star">*</span>
                    </label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      <input 
                        type={showDbPassword ? "text" : "password"} 
                        placeholder="Enter root password" 
                        required 
                        value={templateDbPassword} 
                        onChange={(e) => setTemplateDbPassword(e.target.value)}
                        style={{ width: '100%', padding: '0.65rem 2.5rem 0.65rem 0.85rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-main)', fontSize: '0.9rem' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowDbPassword(!showDbPassword)}
                        style={{ position: 'absolute', right: '0.65rem', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '0.2rem' }}
                        title={showDbPassword ? "Hide password" : "Show password"}
                      >
                        {showDbPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    <p className="form-feedback" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Password for root user.</p>
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginTop: '1.25rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.35rem', display: 'block' }}>
                    Container Specs (vCPU & RAM) <span className="required-star">*</span>
                  </label>
                  <select 
                    value={`${templateCpu}|${templateMemory}`} 
                    onChange={(e) => {
                      const [cpu, memory] = e.target.value.split('|');
                      setTemplateCpu(cpu);
                      setTemplateMemory(memory);
                    }}
                    style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-main)', fontSize: '0.9rem' }}
                  >
                    <option value="0.5|512m">Micro (0.5 vCPU / 512MB RAM)</option>
                    <option value="1.0|1024m">Basic (1 vCPU / 1GB RAM)</option>
                    <option value="2.0|2048m">Standard (2 vCPU / 2GB RAM)</option>
                    <option value="4.0|4096m">Pro (4 vCPU / 4GB RAM)</option>
                  </select>
                </div>

                {templateSelected !== 'custom-mysql' && (
                  <>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.35rem', display: 'block' }}>
                        Public Port (Optional)
                      </label>
                      <input 
                        type="number" 
                        placeholder="Auto-assign" 
                        value={selectedTemplatePort} 
                        onChange={(e) => handlePortChange(e.target.value)} 
                        style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-main)', fontSize: '0.9rem' }}
                      />
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.35rem', display: 'block' }}>
                        Primary App Port (Subdomain)
                      </label>
                      <input 
                        type="number" 
                        placeholder="e.g. 3000" 
                        value={templateInternalPort} 
                        onChange={(e) => setTemplateInternalPort(e.target.value)} 
                        style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-main)', fontSize: '0.9rem' }}
                      />
                      <p className="form-feedback" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Port inside container that the subdomain will route to.</p>
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '0.35rem', display: 'block' }}>
                        Additional Container Ports
                      </label>
                      <input 
                        type="text" 
                        placeholder="e.g. 8080, 5000" 
                        value={templateUserPorts} 
                        onChange={(e) => setTemplateUserPorts(e.target.value)} 
                        style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-main)', fontSize: '0.9rem' }}
                      />
                      <p className="form-feedback" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Comma separated ports your app uses internally.</p>
                    </div>
                  </>
                )}
                
                {templateSelected !== 'custom-mysql' && (
                  <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <Server size={14} /> Additional Subdomains (Entry Points)
                      </label>
                      <button
                        type="button"
                        onClick={handleAddEntryPoint}
                        style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--primary)', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem' }}
                      >
                        <Plus size={12} /> Add Subdomain
                      </button>
                    </div>
                    {entryPoints.length > 0 && (
                      <div style={{ border: '1px solid var(--border)', borderRadius: '0.5rem', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                          <thead style={{ background: 'var(--surface-2)' }}>
                            <tr>
                              <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>Role</th>
                              <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>Port</th>
                              <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>Subdomain</th>
                              <th style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', width: '30px' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {entryPoints.map((ep, idx) => (
                              <tr key={idx}>
                                <td style={{ padding: '0.35rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                                  <input type="text" value={ep.role || ''} placeholder="e.g. api" onChange={(e) => handleUpdateEntryPoint(idx, 'role', e.target.value)} style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)', outline: 'none' }} />
                                </td>
                                <td style={{ padding: '0.35rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                                  <input type="number" value={ep.port || ''} placeholder="8080" onChange={(e) => handleUpdateEntryPoint(idx, 'port', e.target.value)} style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)', outline: 'none' }} />
                                </td>
                                <td style={{ padding: '0.35rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                                  <input type="text" value={ep.domain || ''} placeholder="Auto-assigned" onChange={(e) => handleUpdateEntryPoint(idx, 'domain', e.target.value)} style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)', outline: 'none' }} />
                                </td>
                                <td style={{ padding: '0.35rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                                  <button type="button" onClick={() => handleRemoveEntryPoint(idx)} style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: '0' }}><X size={14} /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {(templateSelected === 'node-mysql' || templateSelected === 'custom-mysql') && (
                  <div className="form-group" style={{ margin: 0 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: enableTemplateVolume ? '0.35rem' : '0' }}>
                      <input 
                        type="checkbox" 
                        checked={enableTemplateVolume}
                        onChange={(e) => setEnableTemplateVolume(e.target.checked)}
                        style={{ width: 'auto', margin: 0 }}
                      />
                      Enable Persistent Volume (MySQL Data)
                    </label>
                    {enableTemplateVolume && (
                      <input 
                        type="text" 
                        placeholder="e.g. mysql_data_volume" 
                        value={templateVolumeName}
                        onChange={(e) => setTemplateVolumeName(e.target.value)}
                        required={enableTemplateVolume}
                        style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-main)', fontSize: '0.9rem', marginTop: '0.5rem' }}
                      />
                    )}
                    <p className="form-feedback" style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Mounts to /var/lib/mysql to ensure data survives restarts.</p>
                  </div>
                )}
              </div>
            </div>

            {(templateConnectionString || templateVscodeLink || templateAntigravityLink) && (
              <div style={{
                padding: '1.25rem 1.5rem',
                background: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981', fontWeight: 600, fontSize: '0.95rem' }}>
                  <CheckCircle2 size={18} />
                  <span>Template Deployed Successfully!</span>
                </div>
                {templateConnectionString && (
                  <>
                    <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                      Your environment is ready. Code connects via <strong>localhost</strong> — zero code changes needed.
                    </p>
                    
                    {templateResolvedDomain && (
                      <div style={{ marginTop: '0.75rem', marginBottom: '0.25rem' }}>
                        <p style={{ margin: '0 0 0.25rem 0', color: 'var(--text-main)', fontSize: '0.85rem', fontWeight: 600 }}>
                          🌐 Web URL (No port required)
                        </p>
                        <div style={{ background: 'var(--surface-3)', padding: '0.65rem 0.85rem', borderRadius: '8px', border: '1px solid var(--border)' }}>
                          <a href={`http://${templateResolvedDomain}`} target="_blank" rel="noreferrer" style={{ color: '#60a5fa', fontSize: '0.85rem', textDecoration: 'none', fontFamily: 'monospace' }}>
                            http://{templateResolvedDomain}
                          </a>
                        </div>
                      </div>
                    )}
                    
                    <p style={{ margin: '1rem 0 0.25rem 0', color: 'var(--text-main)', fontSize: '0.85rem', fontWeight: 600 }}>
                      💻 SSH / Database Connection
                    </p>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      background: 'var(--surface-3)', 
                      padding: '0.65rem 0.85rem', 
                      borderRadius: '8px',
                      border: '1px solid var(--border)',
                      marginTop: '0.25rem'
                    }}>
                      <code style={{ 
                        flex: 1, 
                        color: 'var(--text-main)', 
                        fontSize: '0.82rem',
                        wordBreak: 'break-all'
                      }}>
                        {templateConnectionString}
                      </code>
                      <button 
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(templateConnectionString);
                          onStatusMessage('Connection info copied to clipboard!', 'success');
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          padding: '0.4rem',
                          marginLeft: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          transition: 'color 0.2s ease'
                        }}
                        title="Copy to clipboard"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </>
                )}

                {templateExtraPorts && templateExtraPorts.length > 0 && (
                  <div style={{ marginTop: templateConnectionString ? '1rem' : '0' }}>
                    <p style={{ margin: '0 0 0.5rem 0', color: 'var(--text-main)', fontSize: '0.85rem', fontWeight: 600 }}>
                      ⚡ Available User Ports
                    </p>
                    <div style={{ background: 'var(--surface-3)', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--border)' }}>
                      {templateExtraPorts.map((ep, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: idx < templateExtraPorts.length - 1 ? '0.4rem' : '0' }}>
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>Port {idx + 1}</span>
                          <code style={{ color: 'var(--text-main)', fontSize: '0.85rem' }}>
                            {templateResolvedDomain || window.location.hostname}:{ep.host} <span style={{ color: 'var(--text-muted)' }}>→</span> :{ep.container}
                          </code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {(templateVscodeLink || templateAntigravityLink) && (
                  <div style={{ marginTop: (templateConnectionString || templateExtraPorts) ? '1rem' : '0', position: 'relative' }}>
                    <button
                      className="premium-deploy-btn"
                      onClick={() => setActiveIdeDropdown(!activeIdeDropdown)}
                      style={{
                        background: 'rgba(0, 102, 184, 0.1)',
                        border: '1px solid rgba(0, 102, 184, 0.25)',
                        color: '#0066b8',
                        width: '100%',
                        justifyContent: 'center'
                      }}
                    >
                      <Code size={18} /> <span>Open IDE Options</span>
                    </button>
                    {activeIdeDropdown && (
                      <div 
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          marginTop: '0.4rem',
                          background: 'var(--surface-2)',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 100,
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden'
                        }}
                      >
                        {[
                          { label: 'VS Code', link: templateVscodeLink, color: '#0066b8', icon: <Code size={16} /> },
                          { label: 'Cursor', link: templateCursorLink, color: 'var(--text-main)', icon: <Code size={16} /> },
                          { label: 'Antigravity', link: templateAntigravityLink, color: '#6366f1', icon: <Wand2 size={16} /> }
                        ].filter(ide => ide.link).map(ide => (
                          <a
                            key={ide.label}
                            href="#"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.6rem',
                              padding: '0.75rem 1rem',
                              background: 'transparent',
                              borderBottom: ide.label !== 'Antigravity' ? '1px solid var(--border)' : 'none',
                              color: 'var(--text-main)',
                              textDecoration: 'none',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-3)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            onClick={async (e) => {
                              e.preventDefault();
                              setStartingIde(ide.label);
                              try {
                                await fetch(`/api/apps/${templateAppName}/ide/start`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                                });
                              } catch (err) {
                                console.error("Failed to start IDE", err);
                              }
                              setStartingIde(false);
                              window.location.href = ide.link;
                              setActiveIdeDropdown(false);
                            }}
                          >
                            <span style={{ color: ide.color, display: 'flex', alignItems: 'center' }}>{ide.icon}</span>
                            {startingIde === ide.label ? 'Starting...' : ide.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading || Boolean(templateAppNameError) || !templateAppName.trim()} 
              className="vercel-empty-btn"
              style={{
                width: '100%',
                padding: '0.75rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                background: 'var(--text-main)',
                color: 'var(--bg)',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
              }}
            >
              {loading ? <LoaderCircle size={18} className="deploy-spin" /> : <Rocket size={18} />}
              <span>{loading ? 'Launching Template Stack...' : 'Deploy Template Stack'}</span>
            </button>
          </form>
        </>
      )}
    </div>
  );
}
